/**
 * Facility labels. `facility_alias` (a facilities column) wins wherever a facility is named;
 * campus badges and prefixes then fall back to `configuration.short_name`, and everything to the name.
 */

type LabelFacility = {
  name?: string;
  facility_alias?: string | null;
  configuration?: unknown;
};

function parseConfiguration(configuration: unknown): Record<string, any> {
  if (!configuration) return {};
  if (typeof configuration === 'string') {
    try {
      return JSON.parse(configuration) || {};
    } catch {
      return {};
    }
  }
  return configuration as Record<string, any>;
}

const aliasOf = (facility: LabelFacility) =>
  (typeof facility.facility_alias === 'string' && facility.facility_alias.trim()) || '';

/** The facility's alias, else its name: for selectors and anywhere the full name was shown. */
export function facilityDisplayName(facility: LabelFacility | null | undefined): string {
  if (!facility) return '';
  return aliasOf(facility) || facility.name || '';
}

/** The alias, else `configuration.short_name`, else the name: for compact campus badges and prefixes. */
export function facilityShortLabel(facility: LabelFacility | null | undefined): string {
  if (!facility) return '';
  const shortName = parseConfiguration(facility.configuration).short_name;
  return (
    aliasOf(facility) ||
    (typeof shortName === 'string' && shortName.trim()) ||
    facility.name ||
    ''
  );
}

/** "A15" in a single-facility campus, "North DC · A15" in a multi-facility one. */
export function formatCampusLocation(
  locationName: string,
  facility: LabelFacility | null | undefined,
  isMultiFacility: boolean
): string {
  const label = isMultiFacility ? facilityShortLabel(facility) : '';
  if (!label) return locationName;
  return locationName ? `${label} · ${locationName}` : label;
}

/** `{ name, facility_alias, configuration }` of a campus facility by id, for the label helpers. */
export function facilityLabelById(
  facilities: Array<{
    facilityId: string;
    facilityName: string;
    facilityAlias?: string | null;
    configuration?: unknown;
  }>,
  facilityId: string | null | undefined
): { name: string; facility_alias?: string | null; configuration?: unknown } | null {
  const facility = facilities.find((f) => f.facilityId === facilityId);
  return facility
    ? {
        name: facility.facilityName,
        facility_alias: facility.facilityAlias,
        configuration: facility.configuration
      }
    : null;
}

type FacilityWithCampus = {
  id: number;
  name: string;
  facility_alias?: string | null;
  campus?: { uuid?: string | null; name?: string | null } | null;
};

export interface CampusOption {
  facilityId: number;
  name: string;
  campusUuid: string;
}

// A facility with no campus is a campus of its own.
const campusKey = (facility: FacilityWithCampus | undefined, facilityId: unknown) =>
  facility?.campus?.uuid || `facility:${facilityId}`;

/** One selector option per campus among the user's facilities, keyed by its first facility. */
export function campusOptions(facilities: FacilityWithCampus[]): CampusOption[] {
  return facilities.reduce<CampusOption[]>((options, facility) => {
    const campusUuid = campusKey(facility, facility.id);
    if (!options.some((option) => option.campusUuid === campusUuid)) {
      options.push({
        facilityId: facility.id,
        name: facility.campus?.name || facilityDisplayName(facility),
        campusUuid
      });
    }
    return options;
  }, []);
}

/** The campus of the selected facility, in `campusOptions` keys. */
export function selectedCampusUuid(
  facilities: FacilityWithCampus[],
  selectedFacilityId: number | string | null | undefined
): string {
  return campusKey(
    facilities.find((f) => String(f.id) === String(selectedFacilityId)),
    selectedFacilityId
  );
}
